import kannadatransliterate from "../../kannada";
import offsetCharTable from "./offsetCharTable";

class transliterateFromKannada {
  tableStartChar = 0xc80;
  tableEndChar = 0xcff;
  _targetScript = "kn";

  supportedLangCodes = new Set([
    "kn",
    "en",
    "sa",
    "hi",
    "mr",
    "te",
    "ml",
    "ta",
  ]);

  set language(langCode) {
    this._targetScript = langCode;
  }

  constructor(langCode = null) {
    if (langCode && this._supportedLangCode(langCode)) {
      this._targetScript = langCode;
    } else if (langCode) {
      console.warn("Unsupported Language Code " + langCode);
    }
  }

  _supportedLangCode(langCode) {
    return this.supportedLangCodes.has(langCode);
  }

  transliterate(input, langCode = null) {
    if (!langCode) {
      langCode = this._targetScript;
    }

    if (!this._supportedLangCode(langCode)) {
      console.warn("Unsupported Language " + langCode);
      return input;
    }

    if (langCode === "en") {
      return this._transliterateToISO(input);
    }

    let OFFSET = 0;

    switch (langCode) {
      case "hi":
      case "mr":
      case "sa":
        OFFSET = 0x0900;
        break;

      case "te":
        OFFSET = 0xc00;
        break;

      case "ml":
        OFFSET = 0x0d00;
        break;

      case "ta":
        OFFSET = 0xb80;
        break;
    }

    if (OFFSET === 0) {
      return input;
    }

    OFFSET -= this.tableStartChar;
    let result = offsetCharTable(
      input,
      this.tableStartChar,
      this.tableEndChar,
      OFFSET,
    );

    if (langCode === "ta" || langCode === "ml" || langCode === "sa") {
      result = this._postProcess(result, langCode);
    }

    return result;
  }

  _transliterateToISO(text) {
    let result = kannadatransliterate(
      "IAST",
      "kannada2latin",
      text,
      true,
      false,
    );

    // Anusvarasya ParasavarnadeshaH Post-Processing
    return result
      .replace(/ṃ(?=[kKgGṅ])/g, "ṅ") // Velar (Guttural) - e.g., saṃgha -> saṅgha
      .replace(/ṃ(?=[cCjJñ])/g, "ñ") // Palatal - e.g., caṃcala -> cañcala
      .replace(/ṃ(?=[ṭṬḍḌṇ])/g, "ṇ") // Retroflex (Cerebral) - e.g., kaṃṭha -> kaṇṭha
      .replace(/ṃ(?=[tTdDmn])/g, "n") // Dental - e.g., saṃtata -> santata
      .replace(/ṃ(?=[pPbB])/g, "m"); // Labial - e.g., saṃpatti -> sampatti
  }

  _postProcess(text, langCode) {
    switch (langCode) {
      case "ta":
        return this._postProcessTamil(text);
        break;

      case "ml":
        return this._postProcessMalayalam(text);
        break;

      case "sa":
        return this._postProcessSanskrit(text);
        break;
    }
  }

  _postProcessTamil(text) {
    const reorderPattern =
      /([\u0B95-\u0BB9])([\u00B2\u00B3\u2074])([\u0BBE-\u0BCD]+)/g;
    return (
      text

        // 1. Parasavarna-adesha: Specific nasal families
        .replace(/\u0B82(?=[\u0B95-\u0B98])/g, "ங்") // Ka-varga -> Ng
        .replace(/\u0B82(?=[\u0B9A-\u0B9D])/g, "ஞ்") // Ca-varga -> Ny
        .replace(/\u0B82(?=[\u0B9F-\u0BA2])/g, "ண்") // Ta-varga -> N
        .replace(/\u0B82(?=[\u0BA4-\u0BA7])/g, "ந்") // Tha-varga -> N
        .replace(/\u0B82(?=[\u0BAA-\u0BAD])/g, "ம்") // Pa-varga -> M

        // 2. Anusvara Fallback: Any remaining dot -> Ma + Halant + Nukta (ம்ʼ)
        // This handles cases like Samvatsara, Simha, and word-endings
        // .replace(/\u0B82/g, "\u0BAE\u0BCD\u02BC") // ஂ -> ம்ʼ
        .replace(/\u0B82/g, "\u0BAE\u0BCD") // ஂ -> ம்ʼ

        // 3. Consonant Mapping (Aksharamukha Style with Superscripts)
        // Ka-varga (க)
        .replace(/\u0B96/g, "\u0B95\u00B2") // ಖ (Kha) -> க²
        .replace(/\u0B97/g, "\u0B95\u00B3") // ಗ (Ga) -> க³
        .replace(/\u0B98/g, "\u0B95\u2074") // ಘ (Gha) -> க⁴

        // Ca-varga (ச)
        .replace(/\u0B9B/g, "\u0B9A\u00B2") // ಛ (Cha) -> ச²
        .replace(/\u0B9D/g, "\u0B9C\u00B2") // ಝ (Jha) -> ச⁴

        // Ta-varga (ட)
        .replace(/\u0BA0/g, "\u0B9F\u00B2") // ಠ (Tha) -> ட²
        .replace(/\u0BA1/g, "\u0B9F\u00B3") // ಡ (Da)  -> ட³
        .replace(/\u0BA2/g, "\u0B9F\u2074") // ಢ (Dha) -> ட⁴

        // Tha-varga (த)
        .replace(/\u0BA5/g, "\u0BA4\u00B2") // ಥ (Tha) -> த²
        .replace(/\u0BA6/g, "\u0BA4\u00B3") // ದ (Da)  -> த³
        .replace(/\u0BA7/g, "\u0BA4\u2074") // ಧ (Dha) -> த⁴

        // Pa-varga (ப)
        .replace(/\u0BAB/g, "\u0BAA\u00B2") // ಫ (Pha) -> ப²
        .replace(/\u0BAC/g, "\u0BAA\u00B3") // ಬ (Ba)  -> ப³
        .replace(/\u0BAD/g, "\u0BAA\u2074") // ಭ (Bha) -> ப⁴

        .replace(reorderPattern, "$1$3$2")

        // 5. Ending Na Rule (Dental -> Alveolar)
        .replace(/\u0BA8(?=\s|$)/g, "\u0BA9")
        // Matches \u0BA8\u0BCD followed by either a space OR the end of the string
        .replace(/\u0BA8\u0BCD(?=\s|$)/g, "\u0BA9\u0BCD")

        // 4. Vocalic Akshara and Matra (Ru/Lu substitution with Nukta)
        // Replaces Sanskrit Ṛ/Ṝ with Tamil Ru/Rū + Combining Dot Below
        // Independent Aksharas: ಋ (Ṛ) -> ருʼ | ೠ (Ṝ) -> ரூʼ
        .replace(/\u0B8B/g, "\u0BB0\u0BC1\u02BC") // Ṛ -> ருʼ
        .replace(/\u0B8C/g, "\u0BB0\u0BC2\u02BC") // Ṝ -> ரூʼ

        // Dependent Matras: ೃ (ṛ matra) -> ருʼ | ೄ (ṝ matra) -> ரூʼ
        .replace(/\u0BC3/g, "\u0BB0\u0BC1\u02BC") // ṛ matra -> ருʼ
        .replace(/\u0BC4/g, "\u0BB0\u0BC2\u02BC") // ṝ matra -> ரூʼ

        // Ḷ (ऌ) -> லுʼ | Ḹ (ॡ) -> லூʼ
        .replace(/\u0B8E/g, "\u0BB2\u0BC1\u02BC") // Akshara Ḷ -> லுʼ
        .replace(/\u0B91/g, "\u0BB2\u0BC2\u02BC") // Akshara Ḹ -> லூʼ
        .replace(/\u0BE2/g, "\u0BB2\u0BC1\u02BC") // Matra ḷ -> லுʼ (Note: verify source hex for ḷ matra)
        .replace(/\u0BE3/g, "\u0BB2\u0BC2\u02BC") // Matra ḹ -> லூʼ

        // 6. Visarga -> Ayutha Ezhuthu
        .replace(/\u0B83/g, ":")
    );
  }

  _postProcessMalayalam(text) {
    return text
      .replace(/\u0D28\u0D4D(?=\s|$|[.,!])/g, "ൻ") // n
      .replace(/\u0D30\u0D4D(?=\s|$|[.,!])/g, "ർ") // r
      .replace(/\u0D32\u0D4D(?=\s|$|[.,!])/g, "ൽ") // l
      .replace(/\u0D33\u0D4D(?=\s|$|[.,!])/g, "ൾ") // L
      .replace(/\u0D2 customer\u0D4D(?=\s|$|[.,!])/g, "ൺ"); // N
  }

  _postProcessSanskrit(text) {
    return text // 1. Parasavarna-adesha: Devanagari Nasal Families
      .replace(/\u0902(?=[\u0915-\u0918])/g, "ङ्") // Ka-varga -> ṅ (Velar)
      .replace(/\u0902(?=[\u091A-\u091D])/g, "ञ्") // Ca-varga -> ñ (Palatal)
      .replace(/\u0902(?=[\u091F-\u0922])/g, "ण्") // Ṭa-varga -> ṇ (Retroflex)
      .replace(/\u0902(?=[\u0924-\u0927])/g, "न्") // Ta-varga -> n (Dental)
      .replace(/\u0902(?=[\u092A-\u092D])/g, "म्"); // Pa-varga -> m (Labial)
  }

  transliterateObject(obj) {
    if (obj === null || obj === undefined) return obj;

    if (typeof obj === "string") {
      return this.transliterate(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.transliterateObject(item));
    }

    if (typeof obj === "object") {
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = this.transliterateObject(value);
      }
      return result;
    }

    // for numbers, booleans, functions etc.
    return obj;
  }

  containsKannada(str) {
    return /[\u0C80-\u0CFF]/.test(str);
  }
}

export default transliterateFromKannada;
